import { useState } from 'react';
import { Card, Divider, Space, Button, Typography, Tag, Grid, Modal, Spin, message } from 'antd';
import { TrophyOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CyclePrize } from '../../types/engagement.types';
import { useLotteryResults } from '../../hooks/useLotteryResults';
import LotteryResultsCard from './LotteryResultsCard';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const PRIZE_LABELS: Record<number, string> = { 1: '1º', 2: '2º', 3: '3º', 4: '4º', 5: '5º' };
const BRAND_INDIGO = '#2E2B81';
const BRAND_GREEN = '#00984A';
const BRAND_YELLOW = '#FFC200';
const TINTA_SECUNDARIA = '#595959';

export interface EngagementCycleSummaryData {
  cycleKey: string;
  startsAt: string;
  endsAt: string;
  surveyPoints: number;
  referralPoints: number;
  prizes: CyclePrize[];
  spaRegistrationNumber: string | null;
}

export interface EngagementCycleParticipantProgress {
  totalPoints: number;
  eligibleForPrize: boolean;
  pointsToPrizeThreshold: number;
}

interface EngagementCycleSummaryCardProps {
  cycle: EngagementCycleSummaryData | null | undefined;
  onCtaClick?: () => void;
  ctaLabel?: string;
  participantProgress?: EngagementCycleParticipantProgress | null;
  referralUrl?: string | null;
  lastResultsCycleKey?: string | null;
}

export const EngagementCycleSummaryCard = ({
  cycle,
  onCtaClick,
  ctaLabel = 'Ver Pontuação',
  participantProgress,
  referralUrl,
  lastResultsCycleKey,
}: EngagementCycleSummaryCardProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [resultsOpen, setResultsOpen] = useState(false);
  const { data: lastResults } = useLotteryResults(resultsOpen ? lastResultsCycleKey ?? '' : '');

  const copyReferralLink = async () => {
    if (!referralUrl) return;
    await navigator.clipboard.writeText(referralUrl);
    message.success('Link de indicação copiado!');
  };

  return (
    <>
      <Card
        style={{ background: '#F5F4FF', borderColor: BRAND_INDIGO }}
        styles={{ body: { padding: isMobile ? 20 : '28px 32px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px', color: BRAND_INDIGO, minWidth: 0 }}>
            <TrophyOutlined style={{ fontSize: '28px', color: BRAND_YELLOW }} />
            Programa de Recompensas PePoll
          </div>

          {onCtaClick && (
            <Button type="primary" size="large" style={{ flexShrink: 0 }} onClick={onCtaClick} block={isMobile}>
              {ctaLabel}
            </Button>
          )}
        </div>

        <p style={{ margin: '8px 0 0', color: TINTA_SECUNDARIA, maxWidth: 640 }}>
          Responda pesquisas, indique amigos e concorra a prêmios pelo sorteio da Loteria
          Federal.
        </p>

        {cycle && (
          <>
            <Divider style={{ margin: '16px 0' }} />

            {participantProgress && (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: isMobile ? '14px 16px' : '18px 24px',
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 32, fontWeight: 700, color: BRAND_INDIGO, lineHeight: 1 }}>
                    {participantProgress.totalPoints}
                  </span>
                  <Text type="secondary">ponto{participantProgress.totalPoints !== 1 ? 's' : ''} neste ciclo</Text>
                </div>

                {participantProgress.eligibleForPrize ? (
                  <Tag color={BRAND_GREEN} style={{ color: '#fff' }}>Elegibilidade atingida neste ciclo</Tag>
                ) : participantProgress.pointsToPrizeThreshold > 0 ? (
                  <Tag color={BRAND_YELLOW} style={{ color: '#141414' }}>
                    Faltam {participantProgress.pointsToPrizeThreshold} pontos para participar da premiação
                  </Tag>
                ) : null}
              </div>
            )}

            <div style={{ fontSize: 13 }}>
              <Space direction="vertical" size={4}>
                <Text style={{ color: TINTA_SECUNDARIA, fontWeight: 500 }}>
                  Ciclo {cycle.cycleKey} · {dayjs(cycle.startsAt).format('DD/MM/YYYY')} a {dayjs(cycle.endsAt).format('DD/MM/YYYY')}
                </Text>

                <Text style={{ color: TINTA_SECUNDARIA }}>
                  {cycle.surveyPoints} ponto{cycle.surveyPoints !== 1 ? 's' : ''} por pesquisa
                  {' · '}
                  {cycle.referralPoints} ponto{cycle.referralPoints !== 1 ? 's' : ''} por indicação
                </Text>

                {cycle.prizes.length > 0 && (
                  <div>
                    <Text strong style={{ fontSize: 12, color: TINTA_SECUNDARIA }}>
                      Prêmios deste ciclo
                    </Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                      {cycle.prizes.map((p) => (
                        <div
                          key={p.prizeRank}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #FFE58F',
                            borderRadius: 6,
                            padding: '4px 10px',
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 11.5 }}>
                            {PRIZE_LABELS[p.prizeRank] ?? `${p.prizeRank}º`}
                          </Text>{' '}
                          <Text strong style={{ fontSize: 13.5, color: '#141414' }}>
                            {p.prizeValue}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cycle.spaRegistrationNumber && (
                  <Text style={{ color: TINTA_SECUNDARIA }}>
                    Registro SPA: {cycle.spaRegistrationNumber}
                  </Text>
                )}
              </Space>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
          {referralUrl && (
            <Button icon={<CopyOutlined />} onClick={copyReferralLink} block={isMobile}>
              Copiar link para compartilhar
            </Button>
          )}
          {lastResultsCycleKey && (
            <Button icon={<TrophyOutlined />} onClick={() => setResultsOpen(true)} block={isMobile}>
              Ver resultado do último ciclo
            </Button>
          )}
        </div>
      </Card>

      <Modal
        open={resultsOpen}
        onCancel={() => setResultsOpen(false)}
        footer={null}
        width={640}
        destroyOnHidden
      >
        {lastResults ? (
          <LotteryResultsCard results={lastResults} />
        ) : (
          <Spin style={{ display: 'block', textAlign: 'center', padding: 32 }} />
        )}
      </Modal>
    </>
  );
};

export default EngagementCycleSummaryCard;
